import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import SignupPopup from "./components/SignupPopup";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { RouteLoadingSkeleton } from "./components/RouteLoadingSkeleton";
import Home from "./pages/Home";
import { lazy, Suspense, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { clearStaleBundleRecoveryForCurrentPage } from "./lib/staleBundleRecovery";
import MaintenanceScreen from "./components/MaintenanceScreen";

// Maintenance mode is currently disabled; keep the switch explicit for future repair windows.
export const MAINTENANCE_MODE = false;

// Scroll to top on every route change
function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    const recoveryReset = window.setTimeout(clearStaleBundleRecoveryForCurrentPage, 1200);
    return () => window.clearTimeout(recoveryReset);
  }, [location]);
  return null;
}

const Dispensaries = lazy(() => import("./pages/Dispensaries"));
const Strains = lazy(() => import("./pages/Strains"));
const StrainDetail = lazy(() => import("./pages/StrainDetail"));
const Shop = lazy(() => import("./pages/Shop"));
const Supply = lazy(() => import("./pages/Supply"));
const SupplyCheckout = lazy(() => import("./pages/SupplyCheckout"));
const SupplyOrders = lazy(() => import("./pages/SupplyOrders"));
const SupplyAdmin = lazy(() => import("./pages/SupplyAdmin"));
const Community = lazy(() => import("./pages/Community"));
const MatchConsole = lazy(() => import("./pages/MatchConsole"));
const GroupDetail = lazy(() => import("./pages/GroupDetail"));
const Learn = lazy(() => import("./pages/Learn"));
const Cannabis101Lesson = lazy(() => import("./pages/Cannabis101Lesson"));
const Budtender = lazy(() => import("./pages/Budtender"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const Cart = lazy(() => import("./pages/Cart"));
const Orders = lazy(() => import("./pages/Orders"));
const Pricing = lazy(() => import("./pages/Pricing"));
const VipMembership = lazy(() => import("./pages/VipMembership"));
const DispensaryRegister = lazy(() => import("./pages/DispensaryRegister"));
const DispensaryDashboard = lazy(() => import("./pages/DispensaryDashboard"));
const ReleafaLounge = lazy(() => import("./pages/ReleafaLounge"));
const Events = lazy(() => import("./pages/Events"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const DispensaryDetail = lazy(() => import("./pages/DispensaryDetail"));
const StateLanding = lazy(() => import("./pages/StateLanding"));
const CityLanding = lazy(() => import("./pages/CityLanding"));
const CountryLanding = lazy(() => import("./pages/CountryLanding"));
const ClaimDispensary = lazy(() => import("./pages/ClaimDispensary"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const Referrals = lazy(() => import("./pages/Referrals"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const DirectMessages = lazy(() => import("./pages/DirectMessages"));
const PosComparison = lazy(() => import("./pages/PosComparison"));
const LegalityMap = lazy(() => import("./pages/LegalityMap"));
const MedicalCard = lazy(() => import("./pages/MedicalCard"));
const DispensaryOnboard = lazy(() => import("./pages/DispensaryOnboard"));
const OrderTracking = lazy(() => import("./pages/OrderTracking"));
const StateLandingEnhanced = lazy(() => import("./pages/StateLandingEnhanced"));
const Help = lazy(() => import("./pages/Help"));
const Privacy = lazy(() => import("./pages/Privacy"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const Terms = lazy(() => import("./pages/Terms"));
const DCNeighborhoodLanding = lazy(() => import("./pages/DCNeighborhoodLanding"));
const DCIntentLanding = lazy(() => import("./pages/DCIntentLanding"));
const DCHubPage = lazy(() => import("./pages/DCHubPage"));
const CelebrityCannabishub = lazy(() => import("./pages/CelebrityCannabishub"));
const CelebrityDetail = lazy(() => import("./pages/CelebrityDetail"));
const MusicBlog = lazy(() => import("./pages/MusicBlog"));
const MusicBlogPost = lazy(() => import("./pages/MusicBlogPost"));
const ClaimOffer = lazy(() => import("./pages/ClaimOffer"));
const ActivateTrial = lazy(() => import("./pages/ActivateTrial"));
const MetroLanding = lazy(() => import("./pages/MetroLanding"));
const NeighborhoodLanding = lazy(() => import("./pages/NeighborhoodLanding"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const PosCheckout = lazy(() => import("./pages/PosCheckout"));
const OrderStatus = lazy(() => import("./pages/OrderStatus"));
const Developers = lazy(() => import("./pages/Developers"));
const AboutReleafa = lazy(() => import("./pages/AboutReleafa"));

function PageLoader({ compact = false }: { compact?: boolean }) {
  if (!compact) {
    return <RouteLoadingSkeleton />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    </div>
  );
}

function Router({ compactFallback = false }: { compactFallback?: boolean }) {
  return (
    <Suspense fallback={<PageLoader compact={compactFallback} />}>
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/signup" component={Signup} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/" component={Home} />
        <Route path="/dispensaries" component={Dispensaries} />
        <Route path="/strains" component={Strains} />
        <Route path="/strains/:slug" component={StrainDetail} />
        <Route path="/shop" component={Shop} />
        <Route path="/supply/checkout" component={SupplyCheckout} />
        <Route path="/supply/orders" component={SupplyOrders} />
        <Route path="/admin/supply" component={SupplyAdmin} />
        <Route path="/supply" component={Supply} />
        <Route path="/community/groups/:slug" component={GroupDetail} />
        <Route path="/community" component={Community} />
        <Route path="/match" component={MatchConsole} />
        <Route path="/learn" component={Learn} />
        <Route path="/learn/cannabis-101/:slug" component={Cannabis101Lesson} />
        <Route path="/learn/:slug" component={ArticleDetail} />
        <Route path="/budtender" component={Budtender} />
        <Route path="/dashboard" component={Dashboard} />
        <Route path="/profile" component={Profile} />
        <Route path="/cart" component={Cart} />
        <Route path="/checkout/pos" component={PosCheckout} />
        <Route path="/orders/:orderNumber" component={OrderStatus} />
        <Route path="/orders/:orderId" component={OrderTracking} />
        <Route path="/orders" component={Orders} />
        <Route path="/pricing" component={Pricing} />
        <Route path="/vip" component={VipMembership} />
        <Route path="/best-dispensaries-washington-dc" component={DCHubPage} />
        <Route path="/dispensaries/dc/delivery" component={DCIntentLanding} />
        <Route path="/dispensaries/dc/recreational" component={DCIntentLanding} />
        <Route path="/dispensaries/dc/medical" component={DCIntentLanding} />
        <Route path="/dispensaries/dc/capitol-hill" component={DCNeighborhoodLanding} />
        <Route path="/dispensaries/dc/columbia-heights" component={DCNeighborhoodLanding} />
        <Route path="/dispensaries/dc/adams-morgan" component={DCNeighborhoodLanding} />
        <Route path="/dispensaries/dc/northwest" component={DCNeighborhoodLanding} />
        <Route path="/dispensaries/dc/northeast" component={DCNeighborhoodLanding} />
        <Route path="/dispensaries/dc/southeast" component={DCNeighborhoodLanding} />
        <Route path="/dispensaries/country/:countrySlug/:regionSlug" component={CountryLanding} />
        <Route path="/dispensaries/country/:countrySlug" component={CountryLanding} />
        <Route path="/dispensaries/:state/metro/:metro" component={MetroLanding} />
        <Route path="/dispensaries/:state/:city/:neighborhood" component={NeighborhoodLanding} />
        <Route path="/dispensaries/:state/:city" component={CityLanding} />
        <Route path="/dispensaries/:state" component={StateLandingEnhanced} />
        <Route path="/onboard" component={DispensaryOnboard} />
        <Route path="/dispensary/register" component={DispensaryRegister} />
        <Route path="/claim" component={ClaimDispensary} />
        <Route path="/referrals" component={Referrals} />
        <Route path="/dispensary/dashboard" component={DispensaryDashboard} />
        <Route path="/dispensary/:dispensarySlug/products/:productSlug" component={ProductDetail} />
        <Route path="/dispensary/:slug" component={DispensaryDetail} />
        <Route path="/events" component={Events} />
        <Route path="/lounge" component={ReleafaLounge} />
        <Route path="/messages" component={DirectMessages} />
        <Route path="/pos" component={PosComparison} />
        <Route path="/map" component={LegalityMap} />
        <Route path="/medical-card" component={MedicalCard} />
        <Route path="/admin" component={AdminDashboard} />
        <Route path="/developers" component={Developers} />
        <Route path="/about" component={AboutReleafa} />
        <Route path="/help" component={Help} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/delete-account" component={DeleteAccount} />
        <Route path="/terms" component={Terms} />
        <Route path="/celebrity-cannabis/:slug" component={CelebrityDetail} />
        <Route path="/celebrity-cannabis" component={CelebrityCannabishub} />
        <Route path="/music/:slug" component={MusicBlogPost} />
        <Route path="/music" component={MusicBlog} />
        <Route path="/claim-offer/:token" component={ClaimOffer} />
        <Route path="/activate-trial/:token" component={ActivateTrial} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

const AUTH_PATHS = ["/login", "/signup", "/forgot-password", "/reset-password"];
const FULLSCREEN_PATHS = ["/lounge", "/messages", "/checkout/pos"];

function AppLayout() {
  const [location] = useLocation();
  const isAuthPage = AUTH_PATHS.some(p => location.startsWith(p));
  const isFullscreen = FULLSCREEN_PATHS.some(p => location === p);

  if (isAuthPage || isFullscreen) {
    return (
      <main>
        <Router compactFallback />
      </main>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Router />
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          {MAINTENANCE_MODE ? (
            <MaintenanceScreen />
          ) : (
            <>
              <ScrollToTop />
              <Toaster />
              <SignupPopup />
              <AppLayout />
            </>
          )}
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
